import { useState, useEffect, useCallback, useMemo } from 'react'
import { getTraces, getObservations, deleteTrace } from './langfuseApi'
import { format } from 'date-fns'

function parseMetaString(meta) {
  if (!meta) return {}
  if (typeof meta === 'object') return meta
  try { return JSON.parse(meta) } catch (e) { return {} }
}

function classifyObservation(obs) {
  var name = (obs.name || '').toLowerCase()
  var model = (obs.model || '').toLowerCase()
  if (name.includes('whisper') || name.includes('stt') || name.includes('speech') || name.includes('transcri') || model.includes('whisper')) return 'stt'
  if (name.includes('tts') || name.includes('voice') || name.includes('elevenlabs') || name.includes('synthesis') || model.includes('tts') || model.includes('eleven')) return 'tts'
  if (name.includes('livekit') || name.includes('live_kit') || name.includes('session')) return 'livekit'
  if (model.includes('gpt') || model.includes('claude') || model.includes('gemini') || model.includes('llama') || model.includes('mistral') || name.includes('llm') || name.includes('chat') || name.includes('completion')) return 'llm'
  var hasCost = (obs.totalCost != null ? obs.totalCost : obs.calculatedTotalCost != null ? obs.calculatedTotalCost : obs.cost != null ? obs.cost : 0) > 0
  if (obs.model && hasCost) return 'llm'
  return 'other'
}

function buildTurnFlow(traceObs, traceMeta) {
  var sttObs = null
  var ttsObs = null
  var llmCost = { sum: 0 }

  for (var i = 0; i < traceObs.length; i++) {
    var obs = traceObs[i]
    var svc = classifyObservation(obs)
    var cost = obs.totalCost != null ? obs.totalCost : obs.calculatedTotalCost != null ? obs.calculatedTotalCost : obs.cost != null ? obs.cost : 0
    if (svc === 'stt' && !sttObs) sttObs = obs
    if (svc === 'tts' && !ttsObs) ttsObs = obs
    if (svc === 'llm') llmCost.sum += cost
  }

  var sttMeta = parseMetaString(sttObs && sttObs.metadata)
  var ttsMeta = parseMetaString(ttsObs && ttsObs.metadata)

  return {
    stt: sttObs ? {
      latencyMs: traceMeta.stt_ttfb_ms != null ? traceMeta.stt_ttfb_ms : sttMeta.ttfb_ms != null ? sttMeta.ttfb_ms : (sttObs.latency != null ? sttObs.latency * 1000 : null),
      costUsd: sttObs.totalCost != null ? sttObs.totalCost : sttObs.calculatedTotalCost != null ? sttObs.calculatedTotalCost : 0,
      audioSeconds: sttMeta.audio_seconds != null ? sttMeta.audio_seconds : (sttObs.inputUsage != null ? sttObs.inputUsage / 1000 : null),
      model: sttObs.model || 'whisper-1',
    } : null,
    llm: {
      totalMs: traceMeta.llm_total_ms != null ? traceMeta.llm_total_ms : null,
      ttfbMs: traceMeta.llm_ttfb_ms != null ? traceMeta.llm_ttfb_ms : null,
      nodeLatencies: traceMeta.node_latencies_ms || {},
      toolLatencies: traceMeta.tool_latencies_ms || {},
      costUsd: llmCost.sum,
    },
    tts: ttsObs ? {
      latencyMs: traceMeta.ttfb_ms != null ? traceMeta.ttfb_ms : ttsMeta.ttfb_ms != null ? ttsMeta.ttfb_ms : (ttsObs.latency != null ? ttsObs.latency * 1000 : null),
      costUsd: ttsObs.totalCost != null ? ttsObs.totalCost : ttsObs.calculatedTotalCost != null ? ttsObs.calculatedTotalCost : 0,
      chars: traceMeta.character_count != null ? traceMeta.character_count : ttsMeta.character_count != null ? ttsMeta.character_count : ttsObs.inputUsage != null ? ttsObs.inputUsage : null,
      model: ttsObs.model || 'eleven_multilingual_v2',
    } : null,
  }
}

var CART_TOOLS = new Set(['add_to_cart', 'select_product', 'manage_cart'])
var CHECKOUT_TOOLS = new Set(['checkout', 'confirm_checkout'])

function buildSessions(traces, observations) {
  var obsByTrace = {}
  for (var i = 0; i < observations.length; i++) {
    var obs = observations[i]
    if (!obs.traceId) continue
    if (!obsByTrace[obs.traceId]) obsByTrace[obs.traceId] = []
    obsByTrace[obs.traceId].push(obs)
  }

  var tracesBySession = {}
  for (var j = 0; j < traces.length; j++) {
    var trace = traces[j]
    if (!trace.sessionId) continue
    var sid = trace.sessionId
    if (!tracesBySession[sid]) tracesBySession[sid] = []
    tracesBySession[sid].push(trace)
  }

  return Object.entries(tracesBySession).map(function(entry) {
    var sessionId = entry[0]
    var sessionTraces = entry[1]

    sessionTraces.sort(function(a, b) {
      var ta = a.timestamp ? new Date(a.timestamp).getTime() : 0
      var tb = b.timestamp ? new Date(b.timestamp).getTime() : 0
      return ta - tb
    })

    var allObs = sessionTraces.reduce(function(acc, t) {
      return acc.concat(obsByTrace[t.id] || [])
    }, [])

    var serviceCosts = { stt: 0, tts: 0, livekit: 0, llm: 0, other: 0 }
    var latAcc = {
      stt: { sum: 0, n: 0 },
      tts: { sum: 0, n: 0 },
      llm: { sum: 0, n: 0 },
    }

    for (var ti = 0; ti < sessionTraces.length; ti++) {
      var tr = sessionTraces[ti]
      var traceObs = obsByTrace[tr.id] || []
      var traceMeta = parseMetaString(tr.metadata)

      for (var oi = 0; oi < traceObs.length; oi++) {
        var o = traceObs[oi]
        var cost = o.totalCost != null ? o.totalCost : o.calculatedTotalCost != null ? o.calculatedTotalCost : o.cost != null ? o.cost : 0
        var svc = classifyObservation(o)
        serviceCosts[svc] = (serviceCosts[svc] || 0) + cost
      }

      if (traceMeta.stt_ttfb_ms != null) {
        latAcc.stt.sum += traceMeta.stt_ttfb_ms; latAcc.stt.n++
      } else {
        var sttObs = traceObs.find(function(o) { return classifyObservation(o) === 'stt' })
        if (sttObs && sttObs.latency != null) { latAcc.stt.sum += sttObs.latency * 1000; latAcc.stt.n++ }
      }

      if (traceMeta.ttfb_ms != null) {
        latAcc.tts.sum += traceMeta.ttfb_ms; latAcc.tts.n++
      } else {
        var ttsObs = traceObs.find(function(o) { return classifyObservation(o) === 'tts' })
        if (ttsObs && ttsObs.latency != null) { latAcc.tts.sum += ttsObs.latency * 1000; latAcc.tts.n++ }
      }

      if (traceMeta.llm_total_ms != null) {
        latAcc.llm.sum += traceMeta.llm_total_ms; latAcc.llm.n++
      } else {
        var llmObs = traceObs.filter(function(o) { return classifyObservation(o) === 'llm' })
        for (var li = 0; li < llmObs.length; li++) {
          var lo = llmObs[li]
          if (lo.latency != null && lo.latency > 0) { latAcc.llm.sum += lo.latency * 1000; latAcc.llm.n++ }
        }
      }
    }

    var serviceLatencies = {
      stt: latAcc.stt.n > 0 ? { avgMs: latAcc.stt.sum / latAcc.stt.n, samples: latAcc.stt.n } : null,
      tts: latAcc.tts.n > 0 ? { avgMs: latAcc.tts.sum / latAcc.tts.n, samples: latAcc.tts.n } : null,
      llm: latAcc.llm.n > 0 ? { avgMs: latAcc.llm.sum / latAcc.llm.n, samples: latAcc.llm.n } : null,
    }

    var totalCost = Object.values(serviceCosts).reduce(function(s, v) { return s + v }, 0)

    var startTime = sessionTraces[0] && sessionTraces[0].timestamp ? new Date(sessionTraces[0].timestamp) : null
    var endTime = allObs.reduce(function(latest, obs) {
      var t = obs.endTime ? new Date(obs.endTime) : null
      return t && (!latest || t > latest) ? t : latest
    }, null)

    var durationMin = startTime && endTime ? (endTime - startTime) / 60000 : 0
    var turnCount = sessionTraces.length

    var turns = sessionTraces.map(function(trace, idx) {
      var traceObs = obsByTrace[trace.id] || []
      var traceMeta = parseMetaString(trace.metadata)
      var turnCosts = { stt: 0, tts: 0, livekit: 0, llm: 0, other: 0 }

      for (var oi = 0; oi < traceObs.length; oi++) {
        var obs = traceObs[oi]
        var cost = obs.totalCost != null ? obs.totalCost : obs.calculatedTotalCost != null ? obs.calculatedTotalCost : obs.cost != null ? obs.cost : 0
        var svc = classifyObservation(obs)
        turnCosts[svc] = (turnCosts[svc] || 0) + cost
      }

      var turnTotalCost = Object.values(turnCosts).reduce(function(s, v) { return s + v }, 0)
      var sttObsTurn = traceObs.find(function(o) { return classifyObservation(o) === 'stt' })
      var ttsObsTurn = traceObs.find(function(o) { return classifyObservation(o) === 'tts' })

      return {
        id: trace.id,
        index: idx + 1,
        timestamp: trace.timestamp ? new Date(trace.timestamp) : null,
        serviceCosts: turnCosts,
        totalCost: turnTotalCost,
        turnFlow: buildTurnFlow(traceObs, traceMeta),
        latencies: {
          stt: traceMeta.stt_ttfb_ms != null ? traceMeta.stt_ttfb_ms : (sttObsTurn && sttObsTurn.latency != null ? sttObsTurn.latency * 1000 : null),
          llm: traceMeta.llm_total_ms != null ? traceMeta.llm_total_ms : null,
          tts: traceMeta.ttfb_ms != null ? traceMeta.ttfb_ms : (ttsObsTurn && ttsObsTurn.latency != null ? ttsObsTurn.latency * 1000 : null),
        },
        observations: traceObs,
      }
    })

    var lastTrace = sessionTraces[sessionTraces.length - 1]
    var turnFlow = (turns[turns.length - 1] && turns[turns.length - 1].turnFlow) || buildTurnFlow([], {})

    var addedToCart = false
    var checkedOut = false
    for (var ci = 0; ci < sessionTraces.length; ci++) {
      var toolKeys = Object.keys(parseMetaString(sessionTraces[ci].metadata).tool_latencies_ms || {})
      if (toolKeys.some(function(t) { return CART_TOOLS.has(t) })) addedToCart = true
      if (toolKeys.some(function(t) { return CHECKOUT_TOOLS.has(t) })) checkedOut = true
    }

    var sessionMeta = parseMetaString(lastTrace.metadata)
    var userId = lastTrace.userId || sessionMeta.consumer_id || sessionMeta.device_id || null
    var firstName = sessionMeta.first_name || ''
    var lastName = sessionMeta.last_name || ''
    var consumerId = sessionMeta.consumer_id || ''
    var deviceId = sessionMeta.device_id || ''

    return {
      id: sessionId,
      name: lastTrace.name,
      startTime: startTime,
      durationMin: Math.max(0, durationMin),
      turnCount: turnCount,
      totalCost: totalCost,
      serviceCosts: serviceCosts,
      serviceLatencies: serviceLatencies,
      turnFlow: turnFlow,
      observations: allObs,
      turns: turns,
      metadata: sessionMeta,
      traceCount: sessionTraces.length,
      addedToCart: addedToCart,
      checkedOut: checkedOut,
      userId: userId,
      firstName: firstName,
      lastName: lastName,
      consumerId: consumerId,
      deviceId: deviceId,
    }
  })
}

function processData(rawSessions) {
  if (!rawSessions || rawSessions.length === 0) {
    return {
      sessions: [],
      dailySeries: [],
      svcTotals: { stt: 0, tts: 0, livekit: 0, llm: 0, other: 0 },
      svcLatencies: { stt: null, tts: null, llm: null },
      topSessions: [],
      hourlyActivity: Array.from({ length: 24 }, function(_, h) { return { hour: h, count: 0 } }),
      weekdayActivity: ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(function(d) { return { day: d, count: 0 } }),
      costDistribution: [],
      kpis: {
        totalSessions: 0, totalCost: 0, avgCost: 0,
        totalAudioMin: 0, totalTurns: 0, avgTurnsPerSession: 0, avgDurationMin: 0,
        cartSessions: 0, checkoutSessions: 0,
      },
    }
  }

  var sessions = rawSessions
  var totalCost = 0, totalAudioMin = 0, totalTurns = 0
  var cartSessions = 0, checkoutSessions = 0
  var svcTotals = { stt: 0, tts: 0, livekit: 0, llm: 0, other: 0 }
  var latencyAgg = { stt: { sum: 0, n: 0 }, tts: { sum: 0, n: 0 }, llm: { sum: 0, n: 0 } }

  for (var i = 0; i < sessions.length; i++) {
    var s = sessions[i]
    totalCost += s.totalCost || 0
    totalAudioMin += s.durationMin || 0
    totalTurns += s.turnCount || 0
    if (s.addedToCart) cartSessions++
    if (s.checkedOut) checkoutSessions++
    Object.entries(s.serviceCosts || {}).forEach(function(pair) {
      if (svcTotals[pair[0]] !== undefined) svcTotals[pair[0]] += pair[1]
      else svcTotals.other += pair[1]
    })
    ;['stt', 'tts', 'llm'].forEach(function(svc) {
      var lat = s.serviceLatencies && s.serviceLatencies[svc]
      if (lat && lat.avgMs != null) {
        var n = lat.samples || 1
        latencyAgg[svc].sum += lat.avgMs * n
        latencyAgg[svc].n += n
      }
    })
  }

  var svcLatencies = {}
  ;['stt', 'tts', 'llm'].forEach(function(svc) {
    svcLatencies[svc] = latencyAgg[svc].n > 0
      ? { avgMs: latencyAgg[svc].sum / latencyAgg[svc].n, samples: latencyAgg[svc].n }
      : null
  })

  var avgCost = sessions.length ? totalCost / sessions.length : 0

  var hourlyMap = {}
  for (var h = 0; h < 24; h++) hourlyMap[h] = 0
  for (var si = 0; si < sessions.length; si++) {
    if (sessions[si].startTime) {
      var hr = sessions[si].startTime.getHours()
      hourlyMap[hr] = (hourlyMap[hr] || 0) + 1
    }
  }
  var hourlyActivity = Object.entries(hourlyMap).map(function(pair) { return { hour: Number(pair[0]), count: pair[1] } })

  var DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat']
  var weekdayMap = {}
  DAYS.forEach(function(d) { weekdayMap[d] = 0 })
  for (var wi = 0; wi < sessions.length; wi++) {
    if (sessions[wi].startTime) weekdayMap[DAYS[sessions[wi].startTime.getDay()]]++
  }
  var weekdayActivity = DAYS.map(function(d) { return { day: d, count: weekdayMap[d] } })

  var topSessions = sessions.slice().sort(function(a, b) { return b.totalCost - a.totalCost }).slice(0, 5)

  var BUCKETS = [
    { label: '<$0.01',    min: 0,    max: 0.01 },
    { label: '$0.01-0.05', min: 0.01, max: 0.05 },
    { label: '$0.05-0.10', min: 0.05, max: 0.10 },
    { label: '$0.10-0.25', min: 0.10, max: 0.25 },
    { label: '>$0.25',    min: 0.25, max: Infinity },
  ]
  var costDistribution = BUCKETS.map(function(b) {
    return {
      label: b.label,
      count: sessions.filter(function(s) { return s.totalCost >= b.min && s.totalCost < b.max }).length,
    }
  })

  var dailyServiceMap = {}
  for (var di = 0; di < sessions.length; di++) {
    var sess = sessions[di]
    if (!sess.startTime) continue
    var dk = format(sess.startTime, 'MMM d')
    if (!dailyServiceMap[dk]) {
      dailyServiceMap[dk] = { date: dk, _ts: sess.startTime.getTime(), stt: 0, tts: 0, livekit: 0, llm: 0, other: 0, sessions: 0, turns: 0, totalCost: 0 }
    }
    Object.entries(sess.serviceCosts || {}).forEach(function(pair) {
      if (dailyServiceMap[dk][pair[0]] !== undefined) dailyServiceMap[dk][pair[0]] += pair[1]
      else dailyServiceMap[dk].other += pair[1]
    })
    dailyServiceMap[dk].sessions++
    dailyServiceMap[dk].turns += sess.turnCount || 0
    dailyServiceMap[dk].totalCost += sess.totalCost || 0
  }
  var stackedDailySeries = Object.values(dailyServiceMap)
    .sort(function(a, b) { return a._ts - b._ts })
    .map(function(item) { var copy = Object.assign({}, item); delete copy._ts; return copy })

  return {
    sessions: sessions,
    dailySeries: stackedDailySeries,
    svcTotals: svcTotals,
    svcLatencies: svcLatencies,
    topSessions: topSessions,
    hourlyActivity: hourlyActivity,
    weekdayActivity: weekdayActivity,
    costDistribution: costDistribution,
    kpis: {
      totalSessions: sessions.length,
      totalCost: totalCost,
      avgCost: avgCost,
      totalAudioMin: totalAudioMin,
      totalTurns: totalTurns,
      avgTurnsPerSession: sessions.length ? totalTurns / sessions.length : 0,
      avgDurationMin: sessions.length ? sessions.reduce(function(s, x) { return s + (x.durationMin || 0) }, 0) / sessions.length : 0,
      cartSessions: cartSessions,
      checkoutSessions: checkoutSessions,
    },
  }
}

function readRefreshInterval() {
  var val = localStorage.getItem('lf_refresh_interval') || 'off'
  if (val === 'off') return 0
  var n = parseInt(val, 10)
  return Number.isFinite(n) && n > 0 ? n : 0
}

export function useLangfuseDashboard(dateRange) {
  var [rawSessions, setRawSessions] = useState(null)
  var [fetchState, setFetchState] = useState({ loading: false, error: null, lastFetched: null })
  var [excludedIds, setExcludedIds] = useState(new Set())
  var [deletingIds, setDeletingIds] = useState(new Set())
  var [refreshInterval, setRefreshInterval] = useState(readRefreshInterval)

  useEffect(function() {
    function onStorage() { setRefreshInterval(readRefreshInterval()) }
    window.addEventListener('storage', onStorage)
    return function() { window.removeEventListener('storage', onStorage) }
  }, [])

  var load = useCallback(async function() {
    setFetchState(function(s) { return Object.assign({}, s, { loading: true, error: null }) })
    try {
      var results = await Promise.all([
        getTraces(dateRange.from, dateRange.to),
        getObservations(dateRange.from, dateRange.to),
      ])
      setRawSessions(buildSessions(results[0], results[1]))
      setFetchState({ loading: false, error: null, lastFetched: new Date() })
    } catch (err) {
      setFetchState(function(s) { return Object.assign({}, s, { loading: false, error: err.message || 'Failed to fetch data' }) })
    }
  }, [dateRange])

  useEffect(function() { load() }, [load])

  useEffect(function() {
    if (!refreshInterval) return
    var id = setInterval(load, refreshInterval * 1000)
    return function() { clearInterval(id) }
  }, [load, refreshInterval])

  var data = useMemo(function() {
    if (rawSessions === null) return null
    var visible = excludedIds.size > 0
      ? rawSessions.filter(function(s) { return !excludedIds.has(s.id) })
      : rawSessions
    return processData(visible)
  }, [rawSessions, excludedIds])

  var deleteSession = useCallback(async function(session) {
    var traceIds = (session.turns || []).map(function(t) { return t.id }).filter(Boolean)
    if (traceIds.length === 0) return
    setDeletingIds(function(prev) { return new Set([].concat(Array.from(prev), [session.id])) })
    try {
      await Promise.all(traceIds.map(function(id) { return deleteTrace(id) }))
      setExcludedIds(function(prev) { return new Set([].concat(Array.from(prev), [session.id])) })
    } finally {
      setDeletingIds(function(prev) {
        var next = new Set(prev)
        next.delete(session.id)
        return next
      })
    }
  }, [])

  return {
    data: data,
    loading: fetchState.loading,
    error: fetchState.error,
    lastFetched: fetchState.lastFetched,
    refetch: load,
    deleteSession: deleteSession,
    deletingIds: deletingIds,
  }
}
